import { useState } from 'react';
import {
  Text,
  Badge,
  makeStyles,
  shorthands,
  tokens,
  Button,
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
} from '@fluentui/react-components';
import {
  Box24Regular,
  Clock24Regular,
  Location24Regular,
  ArrowSortUpLines24Regular,
  ArrowSortDownLines24Regular,
  Filter24Regular,
  GroupList24Regular,
  VehicleTruck24Regular,
} from '@fluentui/react-icons';
import JobDetailsModal from '../components/JobDetailsModal';
import Header from '../components/Header';
import { formatBusinessDate } from '../lib/businessTime';

const useStyles = makeStyles({
  // Single elevated workspace card containing the inline Header + kanban
  // board. Mirrors the unified-surface pattern used on /customers and
  // /orders so the page chrome and content read as one continuous surface.
  workspaceCard: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.borderRadius('8px'),
    boxShadow: tokens.shadow4,
    flexGrow: 1,
    // Floor to the viewport so flexGrow can't collapse the card to content
    // height when the nested flex height chain fails to resolve (gray gap
    // below the work surface on scroll). flexShrink:0 prevents re-squeeze.
    minHeight: 'calc(100vh - 64px)',
    flexShrink: 0,
    ...shorthands.margin('8px'),
    ...shorthands.overflow('hidden'),
    // Mobile: full-bleed work surface — drop the margin gutter and rounded
    // corners so the content uses every pixel of width.
    '@media (max-width: 768px)': {
      ...shorthands.margin('0'),
      ...shorthands.borderRadius('0'),
    },
  },
  body: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    minHeight: 0,
    ...shorthands.borderTop('1px', 'solid', tokens.colorNeutralStroke2),
    ...shorthands.padding('12px'),
  },
  kanbanBoard: {
    display: 'flex',
    gap: '12px',
    flexGrow: 1,
    minHeight: 0,
    overflowY: 'auto',
    overflowX: 'hidden',
    // On phones six equal-flex columns collapse to unreadable slivers, so
    // switch to a horizontally scrolling board where each column keeps a real
    // width and snaps into view one at a time (standard mobile kanban pattern).
    '@media (max-width: 768px)': {
      overflowX: 'auto',
      overflowY: 'hidden',
      scrollSnapType: 'x mandatory',
      WebkitOverflowScrolling: 'touch',
    },
  },
  column: {
    display: 'flex',
    flexDirection: 'column',
    flex: '1 1 0',
    minWidth: '0',
    backgroundColor: tokens.colorNeutralBackground2,
    ...shorthands.borderRadius('8px'),
    ...shorthands.padding('12px'),
    height: 'fit-content',
    minHeight: '400px',
    '@media (max-width: 768px)': {
      flex: '0 0 85%',
      minWidth: '260px',
      maxWidth: '320px',
      height: '100%',
      overflowY: 'auto',
      scrollSnapAlign: 'start',
    },
  },
  columnHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '12px',
    ...shorthands.padding('0', '2px'),
  },
  columnTitle: {
    fontSize: '13px',
    fontWeight: '600',
    lineHeight: '1.3',
    wordBreak: 'break-word',
  },
  columnCount: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
    fontWeight: '600',
  },
  columnControls: {
    display: 'flex',
    gap: '4px',
    marginBottom: '8px',
  },
  controlButton: {
    minWidth: 'auto',
    ...shorthands.padding('4px', '8px'),
  },
  groupHeader: {
    fontSize: '11px',
    fontWeight: '600',
    color: tokens.colorNeutralForeground3,
    textTransform: 'uppercase',
    ...shorthands.padding('8px', '2px', '4px', '2px'),
    letterSpacing: '0.5px',
  },
  jobCard: {
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.borderRadius('8px'),
    ...shorthands.padding('10px'),
    marginBottom: '10px',
    boxShadow: tokens.shadow8,
    cursor: 'pointer',
    ...shorthands.border('2px', 'solid', 'transparent'),
    width: '100%',
    boxSizing: 'border-box',
    '&:hover': {
      boxShadow: tokens.shadow8,
      cursor: 'pointer',
    },
    '&:active': {
      cursor: 'grabbing',
    },
  },
  jobCardDragging: {
    opacity: 0.5,
    cursor: 'grabbing',
  },
  jobHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '8px',
    gap: '8px',
  },
  statusBadge: {
    flexShrink: 0,
    whiteSpace: 'nowrap',
    fontSize: '10px',
  },
  jobCustomer: {
    fontSize: '13px',
    fontWeight: '600',
    marginBottom: '4px',
    lineHeight: '1.3',
    wordBreak: 'break-word',
  },
  jobOrderId: {
    fontSize: '11px',
    color: tokens.colorNeutralForeground3,
    marginBottom: '2px',
    display: 'block',
  },
  jobCreatedDate: {
    fontSize: '10px',
    color: tokens.colorNeutralForeground3,
    display: 'block',
  },
  jobDetails: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  jobDetailRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: '11px',
    color: tokens.colorNeutralForeground2,
    lineHeight: '1.2',
  },
  driverInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    marginTop: '6px',
    ...shorthands.padding('6px'),
    backgroundColor: tokens.colorNeutralBackground2,
    ...shorthands.borderRadius('4px'),
  },
  driverName: {
    fontSize: '11px',
    fontWeight: '600',
    lineHeight: '1.2',
    wordBreak: 'break-word',
  },
  driverStats: {
    fontSize: '10px',
    color: tokens.colorNeutralForeground3,
    lineHeight: '1.2',
  },
  dropZone: {
    ...shorthands.border('2px', 'dashed', tokens.colorBrandBackground),
    backgroundColor: tokens.colorBrandBackground2,
    ...shorthands.borderRadius('8px'),
    ...shorthands.padding('12px'),
    marginBottom: '12px',
    minHeight: '80px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  dropZoneText: {
    color: tokens.colorBrandForeground1,
    fontWeight: '600',
  },
});

interface Job {
  id: string;
  orderId: string;
  customer: string;
  type: 'Delivery' | 'Pickup' | 'Exchange';
  dumpsterSize: string;
  address: string;
  time: string;
  status: 'Deliver' | 'Pickup' | 'Service' | 'Swapout' | 'On Site';
  // Location data
  latitude?: number;
  longitude?: number;
  // Extended details
  contactName?: string;
  contactPhone?: string;
  contactEmail?: string;
  rentalStartDate?: string;
  rentalEndDate?: string;
  rentalDuration?: string;
  price?: string;
  depositAmount?: string;
  equipmentType?: string;
  equipmentId?: string;
  specialInstructions?: string;
  accessNotes?: string;
  gateCode?: string;
  permitRequired?: boolean;
  permitNumber?: string;
  booqableBookingId?: string;
  createdDate?: string;
  lastModifiedDate?: string;
}

interface Truck {
  id: string;
  name: string;
  status: 'Online' | 'Offline';
  tracker: string;
  group: string;
  vin: string;
  dashcam: string;
}

interface Waypoint {
  id: string;
  facility: string;
  latitude: number;
  longitude: number;
  plusCode?: string;
  address?: string;
}

// Sample data
const initialJobs: { [key: string]: Job[] } = {
  'truck-1': [
    {
      id: 'job-4',
      orderId: 'ORD-001',
      customer: 'Carmen Rivera Construction',
      type: 'Delivery',
      dumpsterSize: '30-yard',
      address: '123 Calle Sol, San Juan',
      time: '8:00 AM',
      status: 'Deliver',
      latitude: 18.4655,
      longitude: -66.1057,
      contactName: 'Carmen Rivera',
      contactPhone: '(787) 555-0101',
      contactEmail: 'carmen@riveraconstruction.pr',
      rentalStartDate: '2025-10-30',
      rentalEndDate: '2025-11-13',
      rentalDuration: '14 days',
      price: '$650.00',
      depositAmount: '$175.00',
      equipmentType: 'Standard Roll-Off',
      equipmentId: 'DUMPSTER-3015',
      specialInstructions: 'Deliver to construction site. Place in marked area near building entrance. General construction debris.',
      accessNotes: 'Active construction zone - check in with site supervisor upon arrival',
      gateCode: '#2847',
      permitRequired: true,
      permitNumber: 'SJ-2025-2341',
      booqableBookingId: 'BQ-2025-000101',
      createdDate: '2025-10-23',
      lastModifiedDate: '2025-10-29',
    },
    {
      id: 'job-5',
      orderId: 'ORD-004',
      customer: 'Miguel Santos Landscaping',
      type: 'Delivery',
      dumpsterSize: '15-yard',
      address: '321 Calle Verde, Caguas',
      time: '3:30 PM',
      status: 'Swapout',
      latitude: 18.2342,
      longitude: -66.0356,
      contactName: 'Miguel Santos',
      contactPhone: '(787) 555-0156',
      contactEmail: 'miguel@santoslandscaping.com',
      rentalStartDate: '2025-10-30',
      rentalEndDate: '2025-11-03',
      rentalDuration: '4 days',
      price: '$275.00',
      depositAmount: '$100.00',
      equipmentType: 'Compact Roll-Off',
      equipmentId: 'DUMPSTER-1512',
      specialInstructions: 'Weekend landscaping project. Green waste and soil only - no rocks or construction debris.',
      accessNotes: 'Residential property - driveway placement requested by homeowner',
      permitRequired: false,
      booqableBookingId: 'BQ-2025-000156',
      createdDate: '2025-10-26',
      lastModifiedDate: '2025-10-28',
    },
  ],
  'truck-2': [
    {
      id: 'job-6',
      orderId: 'ORD-002',
      customer: 'Luis Martínez Renovation',
      type: 'Pickup',
      dumpsterSize: '20-yard',
      address: '456 Ave Marina, Bayamón',
      time: '10:30 AM',
      status: 'On Site',
      latitude: 18.3996,
      longitude: -66.1553,
      contactName: 'Luis Martínez',
      contactPhone: '(787) 555-0123',
      contactEmail: 'luis@martinezrenovation.pr',
      rentalStartDate: '2025-10-23',
      rentalEndDate: '2025-10-30',
      rentalDuration: '7 days',
      price: '$425.00',
      depositAmount: '$150.00',
      equipmentType: 'Standard Roll-Off',
      equipmentId: 'DUMPSTER-2019',
      specialInstructions: 'Pickup full dumpster. Interior renovation debris - drywall, wood, fixtures. Check for any hazardous materials before transport.',
      accessNotes: 'Residential neighborhood - narrow street, approach from east side only',
      permitRequired: false,
      booqableBookingId: 'BQ-2025-000123',
      createdDate: '2025-10-21',
      lastModifiedDate: '2025-10-30',
    },
  ],
  'truck-3': [
    {
      id: 'job-7',
      orderId: 'ORD-007',
      customer: 'Isabella Torres Builders',
      type: 'Delivery',
      dumpsterSize: '20-yard',
      address: '234 Calle Luna, Arecibo',
      time: '7:30 AM',
      status: 'Deliver',
      latitude: 18.4723,
      longitude: -66.7200,
      contactName: 'Isabella Torres',
      contactPhone: '(787) 555-0201',
      contactEmail: 'isabella@torresbuilders.pr',
      rentalStartDate: '2025-11-01',
      rentalEndDate: '2025-11-08',
      rentalDuration: '7 days',
      price: '$450.00',
      depositAmount: '$150.00',
      equipmentType: 'Standard Roll-Off',
      equipmentId: 'DUMPSTER-2045',
      specialInstructions: 'Residential renovation project. Mixed construction debris.',
      accessNotes: 'Narrow driveway - careful approach required',
      permitRequired: false,
      booqableBookingId: 'BQ-2025-000201',
      createdDate: '2025-10-26',
      lastModifiedDate: '2025-10-29',
    },
    {
      id: 'job-8',
      orderId: 'ORD-008',
      customer: 'Rafael Méndez Demolition',
      type: 'Pickup',
      dumpsterSize: '40-yard',
      address: '567 Ave Industrial, Guaynabo',
      time: '1:00 PM',
      status: 'On Site',
      latitude: 18.3578,
      longitude: -66.1133,
      contactName: 'Rafael Méndez',
      contactPhone: '(939) 555-0212',
      contactEmail: 'rafael@mendezdemolition.com',
      rentalStartDate: '2025-10-15',
      rentalEndDate: '2025-10-31',
      rentalDuration: '16 days',
      price: '$900.00',
      depositAmount: '$250.00',
      equipmentType: 'Heavy Duty Roll-Off',
      equipmentId: 'DUMPSTER-4056',
      specialInstructions: 'Full demolition debris - concrete, steel, and mixed materials.',
      accessNotes: 'Industrial zone - loading dock access',
      gateCode: '#8821',
      permitRequired: true,
      permitNumber: 'GBO-2025-3421',
      booqableBookingId: 'BQ-2025-000212',
      createdDate: '2025-10-12',
      lastModifiedDate: '2025-10-30',
    },
  ],
  'truck-4': [
    {
      id: 'job-9',
      orderId: 'ORD-009',
      customer: 'Ana Vega Properties',
      type: 'Exchange',
      dumpsterSize: '30-yard',
      address: '890 Calle Paz, Carolina',
      time: '10:00 AM',
      status: 'Swapout',
      latitude: 18.3890,
      longitude: -65.9650,
      contactName: 'Ana Vega',
      contactPhone: '(787) 555-0223',
      contactEmail: 'ana@vegaproperties.pr',
      rentalStartDate: '2025-10-22',
      rentalEndDate: '2025-11-05',
      rentalDuration: '14 days',
      price: '$700.00',
      depositAmount: '$200.00',
      equipmentType: 'Standard Roll-Off',
      equipmentId: 'DUMPSTER-3067',
      specialInstructions: 'Swap full dumpster with empty. Property cleanup project.',
      accessNotes: 'Gated community - call ahead for access',
      gateCode: '#5567',
      permitRequired: false,
      booqableBookingId: 'BQ-2025-000223',
      createdDate: '2025-10-19',
      lastModifiedDate: '2025-10-30',
    },
    {
      id: 'job-10',
      orderId: 'ORD-010',
      customer: 'Carlos Reyes Roofing',
      type: 'Delivery',
      dumpsterSize: '15-yard',
      address: '123 Ave Central, Caguas',
      time: '2:30 PM',
      status: 'Swapout',
      latitude: 18.2408,
      longitude: -66.0389,
      contactName: 'Carlos Reyes',
      contactPhone: '(939) 555-0234',
      contactEmail: 'carlos@reyesroofing.com',
      rentalStartDate: '2025-10-31',
      rentalEndDate: '2025-11-04',
      rentalDuration: '4 days',
      price: '$300.00',
      depositAmount: '$100.00',
      equipmentType: 'Compact Roll-Off',
      equipmentId: 'DUMPSTER-1523',
      specialInstructions: 'Roofing shingles and tar paper only.',
      accessNotes: 'Steep driveway - use caution',
      permitRequired: false,
      booqableBookingId: 'BQ-2025-000234',
      createdDate: '2025-10-28',
      lastModifiedDate: '2025-10-29',
    },
  ],
  'truck-5': [
    {
      id: 'job-11',
      orderId: 'ORD-011',
      customer: 'Elena Ortiz Construction',
      type: 'Pickup',
      dumpsterSize: '20-yard',
      address: '456 Calle Mar, Fajardo',
      time: '8:30 AM',
      status: 'Pickup',
      latitude: 18.3258,
      longitude: -65.6525,
      contactName: 'Elena Ortiz',
      contactPhone: '(787) 555-0245',
      contactEmail: 'elena@ortizconstruction.pr',
      rentalStartDate: '2025-10-24',
      rentalEndDate: '2025-10-31',
      rentalDuration: '7 days',
      price: '$425.00',
      depositAmount: '$150.00',
      equipmentType: 'Standard Roll-Off',
      equipmentId: 'DUMPSTER-2078',
      specialInstructions: 'Coastal project - sand and construction debris.',
      accessNotes: 'Beach access road - 4WD recommended',
      permitRequired: true,
      permitNumber: 'FAJ-2025-1567',
      booqableBookingId: 'BQ-2025-000245',
      createdDate: '2025-10-22',
      lastModifiedDate: '2025-10-30',
    },
  ],
  unassigned: [
    {
      id: 'job-1',
      orderId: 'ORD-003',
      customer: 'Sofía Delgado Development',
      type: 'Exchange',
      dumpsterSize: '40-yard',
      address: '789 Calle Principal, Carolina',
      time: '2:00 PM',
      status: 'Swapout',
      latitude: 18.3809,
      longitude: -65.9571,
      contactName: 'Sofía Delgado',
      contactPhone: '(787) 555-0143',
      contactEmail: 'sofia@delgadodev.pr',
      rentalStartDate: '2025-10-28',
      rentalEndDate: '2025-11-11',
      rentalDuration: '14 days',
      price: '$850.00',
      depositAmount: '$200.00',
      equipmentType: 'Heavy Duty Roll-Off',
      equipmentId: 'DUMPSTER-4040',
      specialInstructions: 'Exchange full dumpster with empty one. Heavy concrete debris - ensure proper weight distribution.',
      accessNotes: 'Construction site - enter through main gate on north side',
      gateCode: '#4532',
      permitRequired: true,
      permitNumber: 'CAR-2025-1043',
      booqableBookingId: 'BQ-2025-000143',
      createdDate: '2025-10-25',
      lastModifiedDate: '2025-10-29',
    },
    {
      id: 'job-2',
      orderId: 'ORD-005',
      customer: 'José Ramírez Concrete',
      type: 'Delivery',
      dumpsterSize: '20-yard',
      address: '555 Ave Industrial, Ponce',
      time: '9:00 AM',
      status: 'Deliver',
      latitude: 18.0111,
      longitude: -66.6141,
      contactName: 'José Ramírez',
      contactPhone: '(787) 555-0178',
      contactEmail: 'jose@ramirezconcrete.com',
      rentalStartDate: '2025-10-30',
      rentalEndDate: '2025-11-06',
      rentalDuration: '7 days',
      price: '$425.00',
      depositAmount: '$150.00',
      equipmentType: 'Standard Roll-Off',
      equipmentId: 'DUMPSTER-2023',
      specialInstructions: 'Place near warehouse loading dock. Will contain concrete waste and rebar scraps.',
      accessNotes: 'Industrial complex - dock area accessible from rear entrance',
      permitRequired: false,
      booqableBookingId: 'BQ-2025-000178',
      createdDate: '2025-10-27',
      lastModifiedDate: '2025-10-28',
    },
    {
      id: 'job-3',
      orderId: 'ORD-006',
      customer: 'María González Roofing',
      type: 'Pickup',
      dumpsterSize: '30-yard',
      address: '888 Calle Comercio, Mayagüez',
      time: '11:00 AM',
      status: 'Pickup',
      latitude: 18.2013,
      longitude: -67.1397,
      contactName: 'María González',
      contactPhone: '(787) 555-0192',
      contactEmail: 'maria@gonzalezroofing.pr',
      rentalStartDate: '2025-10-20',
      rentalEndDate: '2025-10-30',
      rentalDuration: '10 days',
      price: '$575.00',
      depositAmount: '$175.00',
      equipmentType: 'Standard Roll-Off',
      equipmentId: 'DUMPSTER-3028',
      specialInstructions: 'Pickup full dumpster containing shingles and roofing materials. Ensure tarp is secured before transport.',
      accessNotes: 'Residential area - dumpster is on street in front of house',
      permitRequired: true,
      permitNumber: 'MAY-2025-0892',
      booqableBookingId: 'BQ-2025-000192',
      createdDate: '2025-10-18',
      lastModifiedDate: '2025-10-29',
    },
    {
      id: 'job-12',
      orderId: 'ORD-012',
      customer: 'Diego Ruiz Landscaping',
      type: 'Delivery',
      dumpsterSize: '10-yard',
      address: '789 Calle Verde, Bayamón',
      time: '9:30 AM',
      status: 'Deliver',
      latitude: 18.4000,
      longitude: -66.1600,
      contactName: 'Diego Ruiz',
      contactPhone: '(939) 555-0256',
      contactEmail: 'diego@ruizlandscaping.com',
      rentalStartDate: '2025-11-02',
      rentalEndDate: '2025-11-05',
      rentalDuration: '3 days',
      price: '$250.00',
      depositAmount: '$75.00',
      equipmentType: 'Compact Roll-Off',
      equipmentId: 'DUMPSTER-1034',
      specialInstructions: 'Yard waste and tree trimmings only.',
      accessNotes: 'Residential area - street parking',
      permitRequired: false,
      booqableBookingId: 'BQ-2025-000256',
      createdDate: '2025-10-29',
      lastModifiedDate: '2025-10-30',
    },
    {
      id: 'job-13',
      orderId: 'ORD-013',
      customer: 'Valentina Cruz Contractors',
      type: 'Exchange',
      dumpsterSize: '30-yard',
      address: '321 Ave Norte, Toa Baja',
      time: '11:30 AM',
      status: 'Swapout',
      latitude: 18.4447,
      longitude: -66.2540,
      contactName: 'Valentina Cruz',
      contactPhone: '(787) 555-0267',
      contactEmail: 'valentina@cruzcontractors.pr',
      rentalStartDate: '2025-10-18',
      rentalEndDate: '2025-11-01',
      rentalDuration: '14 days',
      price: '$675.00',
      depositAmount: '$200.00',
      equipmentType: 'Standard Roll-Off',
      equipmentId: 'DUMPSTER-3089',
      specialInstructions: 'Exchange needed - dumpster at 80% capacity.',
      accessNotes: 'Construction site - active crew on site',
      gateCode: '#3344',
      permitRequired: true,
      permitNumber: 'TB-2025-2789',
      booqableBookingId: 'BQ-2025-000267',
      createdDate: '2025-10-15',
      lastModifiedDate: '2025-10-29',
    },
    {
      id: 'job-14',
      orderId: 'ORD-014',
      customer: 'Marco Silva Renovations',
      type: 'Pickup',
      dumpsterSize: '20-yard',
      address: '654 Calle Sol, Humacao',
      time: '3:00 PM',
      status: 'Pickup',
      latitude: 18.1497,
      longitude: -65.8272,
      contactName: 'Marco Silva',
      contactPhone: '(939) 555-0278',
      contactEmail: 'marco@silvarenovations.com',
      rentalStartDate: '2025-10-20',
      rentalEndDate: '2025-10-30',
      rentalDuration: '10 days',
      price: '$500.00',
      depositAmount: '$150.00',
      equipmentType: 'Standard Roll-Off',
      equipmentId: 'DUMPSTER-2091',
      specialInstructions: 'Kitchen and bathroom remodel debris.',
      accessNotes: 'Narrow street - approach from east side',
      permitRequired: false,
      booqableBookingId: 'BQ-2025-000278',
      createdDate: '2025-10-17',
      lastModifiedDate: '2025-10-29',
    },
    {
      id: 'job-15',
      orderId: 'ORD-015',
      customer: 'Gabriela Morales Builders',
      type: 'Delivery',
      dumpsterSize: '40-yard',
      address: '987 Ave Industrial, Ponce',
      time: '7:00 AM',
      status: 'Deliver',
      latitude: 18.0178,
      longitude: -66.6211,
      contactName: 'Gabriela Morales',
      contactPhone: '(787) 555-0289',
      contactEmail: 'gabriela@moralesbuilders.pr',
      rentalStartDate: '2025-11-03',
      rentalEndDate: '2025-11-17',
      rentalDuration: '14 days',
      price: '$875.00',
      depositAmount: '$250.00',
      equipmentType: 'Heavy Duty Roll-Off',
      equipmentId: 'DUMPSTER-4067',
      specialInstructions: 'Large commercial project - heavy materials expected.',
      accessNotes: 'Loading dock available - rear entrance',
      gateCode: '#7788',
      permitRequired: true,
      permitNumber: 'PON-2025-4512',
      booqableBookingId: 'BQ-2025-000289',
      createdDate: '2025-10-30',
      lastModifiedDate: '2025-10-30',
    },
    {
      id: 'job-16',
      orderId: 'ORD-016',
      customer: 'Santiago López Demolition',
      type: 'Pickup',
      dumpsterSize: '30-yard',
      address: '234 Calle Centro, Mayagüez',
      time: '12:00 PM',
      status: 'Pickup',
      latitude: 18.2056,
      longitude: -67.1453,
      contactName: 'Santiago López',
      contactPhone: '(939) 555-0290',
      contactEmail: 'santiago@lopezdemolition.com',
      rentalStartDate: '2025-10-16',
      rentalEndDate: '2025-10-30',
      rentalDuration: '14 days',
      price: '$625.00',
      depositAmount: '$175.00',
      equipmentType: 'Standard Roll-Off',
      equipmentId: 'DUMPSTER-3012',
      specialInstructions: 'Demolition debris - concrete and rebar.',
      accessNotes: 'Downtown location - coordinate with traffic',
      permitRequired: true,
      permitNumber: 'MAY-2025-1234',
      booqableBookingId: 'BQ-2025-000290',
      createdDate: '2025-10-13',
      lastModifiedDate: '2025-10-29',
    },
    {
      id: 'job-17',
      orderId: 'ORD-017',
      customer: 'Patricia Jiménez Properties',
      type: 'Delivery',
      dumpsterSize: '15-yard',
      address: '567 Ave Marina, Aguadilla',
      time: '10:30 AM',
      status: 'Swapout',
      latitude: 18.4277,
      longitude: -67.1547,
      contactName: 'Patricia Jiménez',
      contactPhone: '(787) 555-0301',
      contactEmail: 'patricia@jimenezproperties.pr',
      rentalStartDate: '2025-11-01',
      rentalEndDate: '2025-11-08',
      rentalDuration: '7 days',
      price: '$350.00',
      depositAmount: '$100.00',
      equipmentType: 'Compact Roll-Off',
      equipmentId: 'DUMPSTER-1545',
      specialInstructions: 'Property cleanout - furniture and household items.',
      accessNotes: 'Beachfront property - sand/salt environment',
      permitRequired: false,
      booqableBookingId: 'BQ-2025-000301',
      createdDate: '2025-10-28',
      lastModifiedDate: '2025-10-30',
    },
    {
      id: 'job-18',
      orderId: 'ORD-018',
      customer: 'Roberto Navarro Roofing',
      type: 'Exchange',
      dumpsterSize: '20-yard',
      address: '890 Calle Nueva, Trujillo Alto',
      time: '1:30 PM',
      status: 'Swapout',
      latitude: 18.3547,
      longitude: -66.0072,
      contactName: 'Roberto Navarro',
      contactPhone: '(939) 555-0312',
      contactEmail: 'roberto@navarroroofing.com',
      rentalStartDate: '2025-10-25',
      rentalEndDate: '2025-11-08',
      rentalDuration: '14 days',
      price: '$550.00',
      depositAmount: '$150.00',
      equipmentType: 'Standard Roll-Off',
      equipmentId: 'DUMPSTER-2023',
      specialInstructions: 'Roofing project - swap when 75% full.',
      accessNotes: 'Hillside location - steep grade',
      permitRequired: false,
      booqableBookingId: 'BQ-2025-000312',
      createdDate: '2025-10-22',
      lastModifiedDate: '2025-10-30',
    },
    {
      id: 'job-19',
      orderId: 'ORD-019',
      customer: 'Alejandra Soto Construction',
      type: 'Delivery',
      dumpsterSize: '30-yard',
      address: '123 Ave Principal, Vega Baja',
      time: '8:00 AM',
      status: 'Deliver',
      latitude: 18.4467,
      longitude: -66.3900,
      contactName: 'Alejandra Soto',
      contactPhone: '(787) 555-0323',
      contactEmail: 'alejandra@sotoconstruction.pr',
      rentalStartDate: '2025-11-02',
      rentalEndDate: '2025-11-16',
      rentalDuration: '14 days',
      price: '$700.00',
      depositAmount: '$200.00',
      equipmentType: 'Standard Roll-Off',
      equipmentId: 'DUMPSTER-3034',
      specialInstructions: 'New construction - mixed materials.',
      accessNotes: 'New development - muddy access road',
      gateCode: '#9921',
      permitRequired: true,
      permitNumber: 'VB-2025-3678',
      booqableBookingId: 'BQ-2025-000323',
      createdDate: '2025-10-29',
      lastModifiedDate: '2025-10-30',
    },
    {
      id: 'job-20',
      orderId: 'ORD-020',
      customer: 'Fernando Castillo Landscaping',
      type: 'Pickup',
      dumpsterSize: '10-yard',
      address: '456 Calle Flores, Manatí',
      time: '4:00 PM',
      status: 'Pickup',
      latitude: 18.4297,
      longitude: -66.4822,
      contactName: 'Fernando Castillo',
      contactPhone: '(939) 555-0334',
      contactEmail: 'fernando@castillolandscaping.com',
      rentalStartDate: '2025-10-27',
      rentalEndDate: '2025-10-30',
      rentalDuration: '3 days',
      price: '$225.00',
      depositAmount: '$75.00',
      equipmentType: 'Compact Roll-Off',
      equipmentId: 'DUMPSTER-1056',
      specialInstructions: 'Green waste only - no soil or rocks.',
      accessNotes: 'Residential neighborhood - quiet hours after 5pm',
      permitRequired: false,
      booqableBookingId: 'BQ-2025-000334',
      createdDate: '2025-10-25',
      lastModifiedDate: '2025-10-29',
    },
    {
      id: 'job-21',
      orderId: 'ORD-021',
      customer: 'Daniela Rivera Contractors',
      type: 'Delivery',
      dumpsterSize: '40-yard',
      address: '789 Ave Comercial, Isabela',
      time: '6:30 AM',
      status: 'Deliver',
      latitude: 18.5006,
      longitude: -67.0242,
      contactName: 'Daniela Rivera',
      contactPhone: '(787) 555-0345',
      contactEmail: 'daniela@riveracontractors.pr',
      rentalStartDate: '2025-11-04',
      rentalEndDate: '2025-11-18',
      rentalDuration: '14 days',
      price: '$925.00',
      depositAmount: '$250.00',
      equipmentType: 'Heavy Duty Roll-Off',
      equipmentId: 'DUMPSTER-4078',
      specialInstructions: 'Major renovation - expect heavy concrete loads.',
      accessNotes: 'Rural location - GPS may be unreliable',
      permitRequired: true,
      permitNumber: 'ISA-2025-5678',
      booqableBookingId: 'BQ-2025-000345',
      createdDate: '2025-10-31',
      lastModifiedDate: '2025-10-31',
    },
    {
      id: 'job-22',
      orderId: 'ORD-022',
      customer: 'Miguel Ángel Pérez Roofing',
      type: 'Exchange',
      dumpsterSize: '25-yard',
      address: '321 Calle Vista, Guayama',
      time: '9:00 AM',
      status: 'Swapout',
      latitude: 17.9847,
      longitude: -66.1136,
      contactName: 'Miguel Ángel Pérez',
      contactPhone: '(939) 555-0356',
      contactEmail: 'miguel@perezroofing.com',
      rentalStartDate: '2025-10-19',
      rentalEndDate: '2025-11-02',
      rentalDuration: '14 days',
      price: '$600.00',
      depositAmount: '$175.00',
      equipmentType: 'Standard Roll-Off',
      equipmentId: 'DUMPSTER-2567',
      specialInstructions: 'Commercial roofing - tar and membrane materials.',
      accessNotes: 'Shopping center location - coordinate with management',
      gateCode: '#4455',
      permitRequired: false,
      booqableBookingId: 'BQ-2025-000356',
      createdDate: '2025-10-16',
      lastModifiedDate: '2025-10-30',
    },
    {
      id: 'job-23',
      orderId: 'ORD-023',
      customer: 'Camila Flores Properties',
      type: 'Pickup',
      dumpsterSize: '15-yard',
      address: '654 Ave Del Mar, Salinas',
      time: '2:00 PM',
      status: 'Pickup',
      latitude: 17.9736,
      longitude: -66.2903,
      contactName: 'Camila Flores',
      contactPhone: '(787) 555-0367',
      contactEmail: 'camila@floresproperties.pr',
      rentalStartDate: '2025-10-21',
      rentalEndDate: '2025-10-30',
      rentalDuration: '9 days',
      price: '$400.00',
      depositAmount: '$125.00',
      equipmentType: 'Compact Roll-Off',
      equipmentId: 'DUMPSTER-1578',
      specialInstructions: 'Apartment cleanout - mixed household items.',
      accessNotes: 'Coastal road - watch for flooding',
      permitRequired: false,
      booqableBookingId: 'BQ-2025-000367',
      createdDate: '2025-10-18',
      lastModifiedDate: '2025-10-29',
    },
    {
      id: 'job-24',
      orderId: 'ORD-024',
      customer: 'Antonio Vargas Construction',
      type: 'Delivery',
      dumpsterSize: '30-yard',
      address: '987 Calle Industrial, Coamo',
      time: '7:30 AM',
      status: 'Swapout',
      latitude: 18.0808,
      longitude: -66.3564,
      contactName: 'Antonio Vargas',
      contactPhone: '(939) 555-0378',
      contactEmail: 'antonio@vargasconstruction.com',
      rentalStartDate: '2025-11-01',
      rentalEndDate: '2025-11-15',
      rentalDuration: '14 days',
      price: '$725.00',
      depositAmount: '$200.00',
      equipmentType: 'Standard Roll-Off',
      equipmentId: 'DUMPSTER-3045',
      specialInstructions: 'Warehouse renovation - metal and wood debris.',
      accessNotes: 'Industrial park - follow signage to building 12',
      gateCode: '#6677',
      permitRequired: true,
      permitNumber: 'CO-2025-2345',
      booqableBookingId: 'BQ-2025-000378',
      createdDate: '2025-10-27',
      lastModifiedDate: '2025-10-30',
    },
    {
      id: 'job-25',
      orderId: 'ORD-025',
      customer: 'Laura Pacheco Demolition',
      type: 'Exchange',
      dumpsterSize: '40-yard',
      address: '234 Ave Norte, Yauco',
      time: '11:00 AM',
      status: 'Swapout',
      latitude: 18.0347,
      longitude: -66.8500,
      contactName: 'Laura Pacheco',
      contactPhone: '(787) 555-0389',
      contactEmail: 'laura@pachecodemolition.pr',
      rentalStartDate: '2025-10-14',
      rentalEndDate: '2025-10-31',
      rentalDuration: '17 days',
      price: '$950.00',
      depositAmount: '$250.00',
      equipmentType: 'Heavy Duty Roll-Off',
      equipmentId: 'DUMPSTER-4089',
      specialInstructions: 'Building demolition - heavy concrete and steel.',
      accessNotes: 'Demolition site - hard hat area',
      permitRequired: true,
      permitNumber: 'YAU-2025-8901',
      booqableBookingId: 'BQ-2025-000389',
      createdDate: '2025-10-11',
      lastModifiedDate: '2025-10-29',
    },
    {
      id: 'job-26',
      orderId: 'ORD-026',
      customer: 'Javier Colón Landscaping',
      type: 'Delivery',
      dumpsterSize: '10-yard',
      address: '567 Calle Jardín, Cidra',
      time: '3:30 PM',
      status: 'Deliver',
      latitude: 18.1756,
      longitude: -66.1611,
      contactName: 'Javier Colón',
      contactPhone: '(939) 555-0390',
      contactEmail: 'javier@colonlandscaping.com',
      rentalStartDate: '2025-11-03',
      rentalEndDate: '2025-11-06',
      rentalDuration: '3 days',
      price: '$275.00',
      depositAmount: '$75.00',
      equipmentType: 'Compact Roll-Off',
      equipmentId: 'DUMPSTER-1067',
      specialInstructions: 'Storm cleanup - tree branches and vegetation.',
      accessNotes: 'Mountain road - winding access',
      permitRequired: false,
      booqableBookingId: 'BQ-2025-000390',
      createdDate: '2025-10-30',
      lastModifiedDate: '2025-10-31',
    },
  ],
};

const trucks: { [key: string]: Truck } = {
  'truck-1': {
    id: 'truck-1',
    name: '5CB-X2P GRAPPLER',
    status: 'Online',
    tracker: '203805812',
    group: 'Roll Off Mack',
    vin: '1M2K189C84M025420',
    dashcam: '003F01AA5B',
  },
  'truck-2': {
    id: 'truck-2',
    name: 'BWI-SYV CHIEF',
    status: 'Online',
    tracker: '212314838',
    group: 'Roll Off Mack',
    vin: '1M2P267C9YM048401',
    dashcam: '003F01AAE9',
  },
  'truck-3': {
    id: 'truck-3',
    name: 'Gringo FL Mack',
    status: 'Online',
    tracker: '88X150200209',
    group: 'Front Loader Mack',
    vin: '1M2AV04CX9M004004',
    dashcam: '00D204E3CB',
  },
  'truck-4': {
    id: 'truck-4',
    name: 'Philly Mack Rolloff',
    status: 'Online',
    tracker: '213202666',
    group: 'Roll Off Mack',
    vin: '1M2P270C9XM046498',
    dashcam: '003F01AB05',
  },
  'truck-5': {
    id: 'truck-5',
    name: 'X2A-YKI BLUE MACK',
    status: 'Offline',
    tracker: '212304361',
    group: 'Front Loader Mack',
    vin: '1M2K189C74M024999',
    dashcam: '003F01AAB7',
  },
};

export const waypoints: Waypoint[] = [
  {
    id: 'wp-1',
    facility: 'EC Waste SJ Transfer',
    plusCode: 'CWM7+8W7',
    address: 'C. Guillermo Ortiz González, San Juan',
    latitude: 18.3983,
    longitude: -66.0636,
  },
  {
    id: 'wp-2',
    facility: 'EC Waste Aguadilla Transfer Station',
    latitude: 18.4275,
    longitude: -67.1541,
  },
  {
    id: 'wp-3',
    facility: 'Conwaste Mayaguez Landfill',
    latitude: 18.2391,
    longitude: -67.1438,
  },
  {
    id: 'wp-4',
    facility: 'Conwaste Arecibo Landfill',
    latitude: 18.4658,
    longitude: -66.6213,
  },
  {
    id: 'wp-5',
    facility: 'Conwaste Guyanabo Transfer Station',
    latitude: 18.3575,
    longitude: -66.1110,
  },
  {
    id: 'wp-6',
    facility: 'Isabela Municipal Landfill',
    plusCode: 'FXH6+69Q',
    address: 'Calle Ramal, Isabela',
    latitude: 18.5006,
    longitude: -67.0242,
  },
  {
    id: 'wp-7',
    facility: 'Conwaste Fajardo Landfill',
    latitude: 18.2913,
    longitude: -65.6793,
  },
  {
    id: 'wp-8',
    facility: 'Conwaste Toa Baja Landfill',
    latitude: 18.4217,
    longitude: -66.2045,
  },
  {
    id: 'wp-9',
    facility: 'EC Waste Caguas Estacion De Transbordo',
    latitude: 18.2341,
    longitude: -66.0485,
  },
  {
    id: 'wp-10',
    facility: 'EC Waste Ponce Landfill',
    latitude: 18.1315,
    longitude: -66.6325,
  },
];

export default function Loadboard() {
  const styles = useStyles();
  const [jobs, setJobs] = useState<{ [key: string]: Job[] }>(initialJobs);
  const [draggedJob, setDraggedJob] = useState<{ job: Job; sourceColumn: string; sourceIndex: number } | null>(null);
  const [dragOverColumn, setDragOverColumn] = useState<string | null>(null);
  const [dragOverJobId, setDragOverJobId] = useState<string | null>(null);
  const [selectedJob, setSelectedJob] = useState<Job | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [filterType, setFilterType] = useState<'All' | 'Delivery' | 'Pickup' | 'Exchange'>('All');
  const [groupByType, setGroupByType] = useState(false);

  const handleDragStart = (e: React.DragEvent, job: Job, sourceColumn: string, sourceIndex: number) => {
    setDraggedJob({ job, sourceColumn, sourceIndex });
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent, columnId: string) => {
    e.preventDefault();
    e.stopPropagation();
    if (dragOverColumn !== columnId) {
      setDragOverColumn(columnId);
    }
  };

  const handleDragOverJob = (e: React.DragEvent, jobId: string) => {
    e.preventDefault();
    e.stopPropagation();
    if (dragOverJobId !== jobId) {
      setDragOverJobId(jobId);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const relatedTarget = e.relatedTarget as HTMLElement;
    const currentTarget = e.currentTarget as HTMLElement;

    // Only clear if we're leaving the column entirely
    if (!currentTarget.contains(relatedTarget)) {
      setDragOverColumn(null);
    }
  };

  const handleDragEnd = () => {
    setDraggedJob(null);
    setDragOverColumn(null);
    setDragOverJobId(null);
  };

  const handleDrop = (e: React.DragEvent, targetColumn: string, targetJobId?: string) => {
    e.preventDefault();
    setDragOverColumn(null);
    setDragOverJobId(null);

    if (!draggedJob) return;

    const { job, sourceColumn } = draggedJob;

    // Don't allow reordering within same column
    if (sourceColumn === targetColumn) {
      setDraggedJob(null);
      return;
    }

    // Moving to different column
    // Remove job from source column
    const sourceJobs = jobs[sourceColumn].filter((j) => j.id !== job.id);
    let targetJobs = [...jobs[targetColumn]];

    // Add to end of column
    targetJobs.push(job);

    setJobs({
      ...jobs,
      [sourceColumn]: sourceJobs,
      [targetColumn]: targetJobs,
    });

    setDraggedJob(null);
  };

  const getStatusColor = (status: string): 'success' | 'danger' | 'important' | 'warning' | 'informative' => {
    switch (status) {
      case 'Deliver':
        return 'success';
      case 'Pickup':
        return 'danger';
      case 'Service':
        return 'important';
      case 'Swapout':
        return 'warning';
      case 'On Site':
        return 'informative';
      default:
        return 'informative';
    }
  };

  const getTypeIcon = (type: string) => {
    return <Box24Regular />;
  };

  const handleJobClick = (job: Job) => {
    setSelectedJob(job);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedJob(null);
  };

  const getProcessedUnassignedJobs = () => {
    let processedJobs = [...(jobs['unassigned'] || [])];

    // Filter by type
    if (filterType !== 'All') {
      processedJobs = processedJobs.filter(job => job.type === filterType);
    }

    // Sort by creation date
    processedJobs.sort((a, b) => {
      const dateA = new Date(a.createdDate || '').getTime();
      const dateB = new Date(b.createdDate || '').getTime();
      return sortOrder === 'asc' ? dateA - dateB : dateB - dateA;
    });

    return processedJobs;
  };

  const getGroupedJobs = (jobsList: Job[]) => {
    if (!groupByType) {
      return { ungrouped: jobsList };
    }

    const grouped: { [key: string]: Job[] } = {};
    jobsList.forEach(job => {
      if (!grouped[job.type]) {
        grouped[job.type] = [];
      }
      grouped[job.type].push(job);
    });

    return grouped;
  };

  const renderJobCard = (job: Job, columnId: string, index: number) => {
    const isDragging = draggedJob?.job.id === job.id;
    const isDraggedOver = dragOverJobId === job.id && draggedJob?.job.id !== job.id;

    return (
      <div
        key={job.id}
        className={`${styles.jobCard} ${isDragging ? styles.jobCardDragging : ''}`}
        draggable
        onDragStart={(e) => handleDragStart(e, job, columnId, index)}
        onDragEnd={handleDragEnd}
        onDragOver={(e) => handleDragOverJob(e, job.id)}
        onDrop={(e) => handleDrop(e, columnId, job.id)}
        onClick={() => handleJobClick(job)}
        style={{
          borderTop: isDraggedOver ? `2px solid #0078d4` : undefined,
        }}
      >
        <div className={styles.jobHeader}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <Text className={styles.jobCustomer}>{job.customer}</Text>
            <Text className={styles.jobOrderId}>{job.orderId}</Text>
            {job.createdDate && (
              <Text className={styles.jobCreatedDate}>
                Created: {formatBusinessDate(job.createdDate)}
              </Text>
            )}
          </div>
          <Badge appearance="tint" color={getStatusColor(job.status)} className={styles.statusBadge}>
            {job.status}
          </Badge>
        </div>

        <div className={styles.jobDetails}>
          <div className={styles.jobDetailRow}>
            {getTypeIcon(job.type)}
            <Text>
              {job.type} • {job.dumpsterSize}
            </Text>
          </div>

          <div className={styles.jobDetailRow}>
            <Clock24Regular style={{ fontSize: '16px' }} />
            <Text>{job.time}</Text>
          </div>

          <div className={styles.jobDetailRow}>
            <Location24Regular style={{ fontSize: '16px' }} />
            <Text>{job.address}</Text>
          </div>
        </div>
      </div>
    );
  };

  const renderUnassignedColumn = () => {
    const columnId = 'unassigned';
    const processedJobs = getProcessedUnassignedJobs();
    const groupedJobs = getGroupedJobs(processedJobs);
    const isDropZoneActive = dragOverColumn === columnId && draggedJob !== null && draggedJob.sourceColumn !== columnId;

    return (
      <div
        className={styles.column}
        onDragOver={(e) => handleDragOver(e, columnId)}
        onDragLeave={(e) => handleDragLeave(e)}
        onDrop={(e) => handleDrop(e, columnId)}
      >
        <div className={styles.columnHeader}>
          <div style={{ width: '100%' }}>
            <Text className={styles.columnTitle}>Unassigned Jobs ({processedJobs.length})</Text>
          </div>
        </div>

        {/* Controls */}
        <div className={styles.columnControls}>
          <Button
            appearance="subtle"
            size="small"
            className={styles.controlButton}
            icon={sortOrder === 'asc' ? <ArrowSortUpLines24Regular /> : <ArrowSortDownLines24Regular />}
            onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
            title={`Sort by date (${sortOrder === 'asc' ? 'oldest first' : 'newest first'})`}
          />
          <Menu>
            <MenuTrigger disableButtonEnhancement>
              <Button
                appearance="subtle"
                size="small"
                className={styles.controlButton}
                icon={<Filter24Regular />}
                title="Filter by type"
              >
                {filterType}
              </Button>
            </MenuTrigger>
            <MenuPopover>
              <MenuList>
                <MenuItem onClick={() => setFilterType('All')}>All</MenuItem>
                <MenuItem onClick={() => setFilterType('Delivery')}>Delivery</MenuItem>
                <MenuItem onClick={() => setFilterType('Pickup')}>Pickup</MenuItem>
                <MenuItem onClick={() => setFilterType('Exchange')}>Exchange</MenuItem>
              </MenuList>
            </MenuPopover>
          </Menu>
          <Button
            appearance={groupByType ? 'primary' : 'subtle'}
            size="small"
            className={styles.controlButton}
            icon={<GroupList24Regular />}
            onClick={() => setGroupByType(!groupByType)}
            title="Group by type"
          />
        </div>

        {isDropZoneActive && (
          <div className={styles.dropZone}>
            <Text className={styles.dropZoneText}>Drop job here</Text>
          </div>
        )}

        {/* Render jobs */}
        {Object.entries(groupedJobs).map(([groupName, groupJobs]) => (
          <div key={groupName}>
            {groupByType && <Text className={styles.groupHeader}>{groupName}</Text>}
            {groupJobs.map((job, index) => renderJobCard(job, columnId, index))}
          </div>
        ))}
      </div>
    );
  };

  const renderColumn = (columnId: string, title: string, truck?: Truck) => {
    const columnJobs = jobs[columnId] || [];
    const isDropZoneActive = dragOverColumn === columnId && draggedJob !== null && draggedJob.sourceColumn !== columnId;

    return (
      <div
        className={styles.column}
        onDragOver={(e) => handleDragOver(e, columnId)}
        onDragLeave={(e) => handleDragLeave(e)}
        onDrop={(e) => handleDrop(e, columnId)}
      >
        <div className={styles.columnHeader}>
          <div style={{ width: '100%' }}>
            {truck ? (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <VehicleTruck24Regular style={{ fontSize: '20px', flexShrink: 0 }} />
                  <Text className={styles.driverName}>{truck.name}</Text>
                </div>
                <Text className={styles.driverStats}>
                  {columnJobs.length} jobs • {truck.status}
                </Text>
              </>
            ) : (
              <Text className={styles.columnTitle}>{title} ({columnJobs.length})</Text>
            )}
          </div>
        </div>

        {isDropZoneActive && (
          <div className={styles.dropZone}>
            <Text className={styles.dropZoneText}>Drop job here</Text>
          </div>
        )}

        {columnJobs.map((job, index) => renderJobCard(job, columnId, index))}
      </div>
    );
  };

  return (
    <>
      {/* marginTop is inline because app.css's .main-content > :first-child rule
          overrides class-level margin with `margin: 0 8px` (no vertical). Inline
          style trumps the class selector to give the card 8px breathing room
          above, matching the 8px on the sides. */}
      <div className={styles.workspaceCard}>
        <Header
          inline
          customTitle={
            <span style={{
              fontSize: tokens.fontSizeBase500,
              fontWeight: tokens.fontWeightSemibold,
              lineHeight: tokens.lineHeightBase300,
              color: tokens.colorNeutralForeground1,
            }}>
              Loadboard
            </span>
          }
        />
        <div className={styles.body}>
          <div className={styles.kanbanBoard}>
            {renderUnassignedColumn()}
            {renderColumn('truck-1', trucks['truck-1'].name, trucks['truck-1'])}
            {renderColumn('truck-2', trucks['truck-2'].name, trucks['truck-2'])}
            {renderColumn('truck-3', trucks['truck-3'].name, trucks['truck-3'])}
            {renderColumn('truck-4', trucks['truck-4'].name, trucks['truck-4'])}
            {renderColumn('truck-5', trucks['truck-5'].name, trucks['truck-5'])}
          </div>
        </div>
      </div>

      <JobDetailsModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        job={selectedJob}
      />
    </>
  );
}
